import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Mail, Send, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thanks! We'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="contact-page">
      {/* Top nav */}
      <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
            roster<span className="text-[#002FA7]">.ai</span>
          </Link>
          <Link
            to="/"
            className="text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 flex items-center gap-1.5"
            data-testid="contact-back-link"
          >
            <ArrowLeft className="w-3 h-3" /> Back home
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="mb-10">
          <div className="w-12 h-12 bg-[#002FA7]/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-[#002FA7]" />
          </div>
          <h1 className="text-4xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">
            Get in touch
          </h1>
          <p className="text-base text-zinc-600 dark:text-zinc-400 mt-2">
            Questions, partnership ideas, billing issues — drop us a note and the team will reply within one business day.
          </p>
        </div>

        {done ? (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded p-8 text-center" data-testid="contact-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Message received</h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              We've passed your message to the team. Expect a reply at <span className="font-medium">{form.email}</span> shortly.
            </p>
            <Button
              onClick={() => { setDone(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
              variant="outline"
              className="mt-5"
              data-testid="contact-send-another"
            >
              Send another
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded p-6 sm:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Your name *
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="subject" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Subject
              </Label>
              <Input
                id="subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="What's this about?"
                className="mt-1"
                data-testid="contact-subject"
              />
            </div>
            <div>
              <Label htmlFor="message" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Message *
              </Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={6}
                placeholder="Tell us how we can help…"
                className="mt-1"
                data-testid="contact-message"
              />
              <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{form.message.length}/5000</p>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="contact-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send message</>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Contact;
